import { MrfiktivHttpClientProvider } from "../../mrfiktiv-http-client.provider";
import {
  MrfiktivBaseChartItemListViewmodelGen,
  MrfiktivBaseTrendAnalysisViewmodelGen,
  MrfiktivInvoiceChartItemListViewModelGen,
  MrfiktivInvoiceTypeItemListViewModelGen,
  MrfiktivPageFilterElementGen,
  MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen,
  MrfiktivStatusDistributionAnalyticsViewModelGen,
  MrfiktivStatusValueDistributionAnalyticsViewModelGen,
  MrfiktivTotalDeductibleItemListViewModelGen
} from "../../v1/data-contracts";
import { FleetAnalytics } from "../../v1/FleetAnalytics";
import { HttpClient } from "../../v1/http-client";
import { IPageFilterElement } from "@/models/page-filter-element.entity";

class FleetAnalyticsService {
  /**
   * The HttpClient containing the Axios Instance
   */
  client: HttpClient;

  /**
   * The proxy.
   */
  proxy: FleetAnalytics;

  /**
   * @class Initialize ThgService
   */
  constructor(clientProvider: MrfiktivHttpClientProvider) {
    this.client = clientProvider.client();
    this.proxy = new FleetAnalytics(this.client);
  }

  /**
   *
   * Gets the invoices net sum per damage type in a time range
   *
   * @returns
   */
  async getTotalSubtotalPerDamageType(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivInvoiceTypeItemListViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerGetTotalSubtotalByDamageType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the invoices net sum per coverage type in a time range
   *
   * @returns
   */
  async getTotalSubtotalPerCoverageType(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivInvoiceTypeItemListViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerGetTotalSubtotalByCoverageType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the coverage type subtotal and deductible cost
   *
   * @returns
   */
  async getCoverageSubtotalDeductible(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivTotalDeductibleItemListViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerGetSubtotalByCoverageTypeWichDeductible(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the amount of reports per damge type in a time range
   *
   * @returns
   */
  async getCountPerDamageType(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerDamageType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the amount of reports per coverage type in a time range
   *
   * @returns
   */
  async getCountPerCoverageType(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerCoverageType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }
  /**
   *
   * Gets the amount of reports per trip type in a time range
   *
   * @returns
   */
  async getCountPerTripType(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerTripType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the report status value distribution data
   *
   * @returns
   */
  async getStatusValueDistribution(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivStatusValueDistributionAnalyticsViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerStatusValueDistribution(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the report progress status distribution data
   *
   * @returns
   */
  async getProgressStatusDistribution(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivStatusDistributionAnalyticsViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerStatusDistribution(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the report `workshopCompanyId` associated with flag `isAffiliate`
   *
   * @returns
   */
  async getWorkshopCompanyDistribution(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerReportWorkshopCompanyDistribution(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the weekday distribution data
   *
   * @returns
   */
  async getWeekdayAnalysis(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivReportWeeklyAnalysisPerPartnerViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerReportWeekdayAnalysisPerPartner(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the average of damge type total
   *
   * @returns
   */
  async getDamageTypeAverage(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerDamageTypeAverage(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the average of coverage type total
   *
   * @returns
   */
  async getCoverageTypeAverage(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseChartItemListViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerCoverageType(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the top creditor data
   *
   * @returns
   */
  async getTotalSubtotalBySeller(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivInvoiceChartItemListViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerGetTotalSubtotalByInvoiceSeller(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the TrendAnalytics Data
   *
   * @returns
   */
  async getTrendAnalysisDataPerPartnerFleetId(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivBaseTrendAnalysisViewmodelGen> {
    const response = await this.proxy.fleetAnalyticsControllerTrendAnalysisForPartner(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }

  /**
   *
   * Gets the top vehicle data
   *
   * @returns
   */
  async getTotalSubtotalByVehicle(
    partnerId: string,
    fleetId: string,
    from: string,
    to: string,
    ids: string[] = [fleetId],
    filters?: IPageFilterElement[]
  ): Promise<MrfiktivInvoiceChartItemListViewModelGen> {
    const response = await this.proxy.fleetAnalyticsControllerGetTotalSubtotalByVehicle(
      {
        partnerId,
        fleetId,
        from,
        to,
        filter: filters?.map(f => ({
          key: f.key,
          operation: f.operation as MrfiktivPageFilterElementGen["operation"],
          value: f.value.toString()
        }))
      },
      {
        ids
      }
    );

    return response.data;
  }
}

export default new FleetAnalyticsService(new MrfiktivHttpClientProvider());
